import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import StyledButton from "../../../../common/models/Button";
import { appUrl } from "../../../../../utils/config";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import { he } from "date-fns/locale";
import EditIcon from "@material-ui/icons/Edit";

const useStyles = makeStyles((theme) => ({
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "900px", // Increased width to accommodate new column
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
   resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "2px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  backButton: {
    backgroundColor: "#aaa",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
  },
  dropdown: {
    minWidth: "300px",
    "& .MuiSelect-select": {
      padding: "6px 8px",
      fontSize: "14px",
    },
  },
  disabledDropdown: {
    "& .MuiSelect-select": {
      color: "#999",
      backgroundColor: "#f5f5f5",
    },
  },
  enabledDropdown: {
    "& .MuiSelect-select": {
      border: "1px solid #000",
    }
  }
}));

const DocumentTypeTab = (props) => {
  const { featuresAccess, sponsorId } = props;
  const classes = useStyles();
  const [documentTypes, setDocumentTypes] = useState([]); // State to store document types
  const [documentCategories, setDocumentCategories] = useState([]); // State to store document categories
  const [error, setError] = useState(""); // State to handle errors
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingStates, setSavingStates] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  
 
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const dispatch = (action) => {
    if (action.type === "OPEN_BACKDROP") {
      //console.log("Opening backdrop");
    } else if (action.type === "DESTROY_BACKDROP") {
      //console.log("Closing backdrop");
    }
  };

  const fetchDocumentCategories = async () => {
    setLoading(true);
    setError("");
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDocumentCategories/false`;
      const response = await axios.get(url);
      
      if (response.data?.status === true || response.data?.status === "true") {
        const documentCategoryList = response.data?.documentCategoryList || [];
        const mappedDocumentCategories = documentCategoryList.map((doc) => ({
          id: doc.documentCategoryID,
          name: doc.documentCategoryName,
          selected: !!doc.activated,
          isNew: false,
          type: doc.documentCategoryType,
          lastUpdatedBy: doc.lastUpdatedBy,
        }));
        setDocumentCategories(mappedDocumentCategories);
      } else {
        console.error("Failed to fetch document categories:", response.data?.message);
        setError(response.data?.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching document categories:", error);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTypes = async () => {
    setLoading(true); // Show the loading spinner
    setError(""); // Reset error state before making the API call
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDocumentTypes/false`; // API endpoint
      const response = await axios.get(url);

      //console.log("API response:", response.data); // Log full response for debugging

      if (response.data?.status === true || response.data?.status === "true") {
        // Check if documentTypeList exists and is an array
        const documentTypesList = response.data?.data?.documentTypesList || [];

        //console.log("Fetched Document Types:", documentTypesList);

        // Map the API response to match the expected structure
        const mappedDocumentTypes = documentTypesList.map((doc) => ({
          id: doc.documentTypeID, // Map documentTypeID to id with fallback
          name: doc.documentTypeName, // Map documentTypeName to name with fallback
          selected: !!doc.activated, // Map activated to selected, convert to boolean
          isNew: false,
          newDocumentTypeName: doc.newDocumentTypeName,
          type: doc.documentType,
          lastUpdatedBy: doc.lastUpdatedBy,
          selectedCategory: doc.documentCategoryID, // Add category selection
          // Default to false for fetched document types
        }));

        setDocumentTypes(mappedDocumentTypes); // Update state with mapped document types
      } else {
        console.error(
          "Failed to fetch document types:",
          response.data?.message
        );
        setError(
          response.data?.message || "Something went wrong. Please try again."
        ); // Set error message
      }
    } catch (error) {
      console.error("Error fetching document types:", error);
      setError("Something went wrong. Please try again."); // Set error message
    } finally {
      setLoading(false); // Hide the loading spinner
    }
  };

  useEffect(() => {
    fetchDocumentTypes(); // Fetch document types on component mount
    fetchDocumentCategories(); // Fetch document categories on component mount
  }, []);

  const saveDocumentType = async (index) => {
    const documentToSave = documentTypes[index]; // Get the document type to save
    if (!documentToSave.name?.trim() || savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      if (!documentToSave.name.trim()) {
        setError("Please provide a name for the document type.");
        return;
      }

      // Validate that a category is selected
      if (!documentToSave.selectedCategory) {
        setError("Please select a document category.");
        setSavingStates((prev) => ({ ...prev, [index]: false }));
        return;
      }

      // Prepare the payload for the API
      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        documentTypesList: [
          {
            documentTypeName: documentToSave.name.trim(), // Trim leading and trailing spaces
            documentType: "New", // Type of the document
            activated: documentToSave.selected, // Default to true if not set
            documentCategoryID: documentToSave.selectedCategory, // Add category ID
          },
        ],
      };


      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addDocumentTypes`; // API endpoint
      const response = await axios.post(url, payload);
      fetchDocumentTypes();

      if (response.data?.status === true || response.data?.status === "true") {
        //console.log("Document type saved successfully:", response.data);
 props.setDocumentType((prev) => !prev);
        // Update the state to mark the document type as saved
        setDocumentTypes((prev) =>
          prev.map((doc, i) =>
            i === index
              ? { ...doc, id: response.data?.data?.id, isNew: false }
              : doc
          )
        );
      } else {
        console.error("Failed to save document type:", response.data?.message);
        setError(response.data?.message || "Failed to save document type.");
      }
    } catch (error) {
      console.error("Error saving document type:", error);
      setError("An error occurred while saving the document type.");
    } finally {
      setSavingStates((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleToggle = (index) => {
    const newList = [...documentTypes];
    newList[index].selected = !newList[index].selected;
    newList[index].isUpdated = true;
    setDocumentTypes(newList);
    setIsUpdated(true);
    
  };

  const handleNameChange = (index, value) => {
    const newList = [...documentTypes];
    newList[index].name = value.trim(); // Trim leading and trailing spaces
    setDocumentTypes(newList);
    setIsUpdated(true);
  };

  const handleDocumentCategoryChange = (index, categoryId) => {
    const newList = [...documentTypes];
    newList[index].selectedCategory = categoryId;
    newList[index].isUpdated = true;
    setDocumentTypes(newList);
    setIsUpdated(true);
  };

  // const handleDelete = (index) => {
  //   const newList = documentTypes.filter((_, i) => i !== index);
  //   setDocumentTypes(newList);
  //   setIsUpdated(true);
  // };

  const handleAddNew = () => {
    //const nextId = (documentTypes.length + 1).toString().padStart(2, "0");
    setDocumentTypes([
      ...documentTypes,
      {
        // id: nextId,
        name: "",
        selected: true,
        isNew: true,
        selectedCategory: "", // Add default category selection
      },
    ]);
    setIsUpdated(true);
  };

  // const handleNextClick = async () => {
  //   try {
  //     // Check if at least one toggle is turned on
  //     const isAnyToggleOn = documentTypes.filter((doc) => doc.isUpdated);

  //     if (!isAnyToggleOn.length) {
  //       console.log("No updates detected. Proceeding to the next step.");
  //       props.handleNext(); // Directly move to the next tab
  //       return;
  //     }
  //     if (documentTypes.every((doc) => !doc.selected)) {
  //       setErrorMessage("Please enable at least one in toggle mode.");
  //       return;
  //     }
  //     setErrorMessage("");

  //     // Check if any new document type has an empty name
  //     // const hasEmptyName = documentTypes.some(
  //     //   (doc) => doc.isNew && !doc.name.trim()
  //     // );
  //     // if (hasEmptyName) {
  //     //   setErrorMessage("Please provide a name for all new document types.");
  //     //   return;
  //     // }

  //     if (isUpdated) {
  //       // Prepare payload for API
  //       const payload = {
  //         sponsorId: 2, // Example sponsorId
  //         ecosystemId: 1,
  //         documentTypesList: isAnyToggleOn.map((doc) => ({
  //           documentTypeID: doc.id,
  //           documentTypeName: doc.name,
  //           activated: doc.selected,
  //           markForDelete: false,
  //           // Add any other required fields based on your API requirements
  //         })),
  //       };

  //       // Call API to update document types
  //       dispatch({ type: "OPEN_BACKDROP" });
  //       const url = `${appUrl}/Pharma%20Contract%20Manufacturing/N1/sh1/Pharma%20Co/Admin/updateDocumentTypes`;
  //       const response = await axios.post(url, payload);

  //       if (
  //         response.data?.status === true ||
  //         response.data?.status === "true"
  //       ) {
  //         console.log("Document types updated successfully");

  //         // Move to next tab if props.handleNext exists
  //         props.handleNext();
  //       } else {
  //         setErrorMessage(
  //           response.data?.message || "Failed to update document types"
  //         );
  //       }
  //     } else {
  //       // If no updates were made, just move to the next tab
  //       if (props.handleNext) {
  //         props.handleNext();
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error saving document types:", error);
  //     setErrorMessage("An error occurred while saving. Please try again.");
  //   } finally {
  //     dispatch({ type: "DESTROY_BACKDROP" });
  //   }
  // };
  const handleSaveChanges = async () => {
    try {
      setSaveLoading(true);
      // Check if at least one toggle is turned on
      const isAnyToggleOn = documentTypes.filter((doc) => doc.isUpdated);

      if (!isAnyToggleOn.length) {
        //console.log("No updates detected. Proceeding to the next step.");
      
        return;
      }
      if (documentTypes.every((doc) => !doc.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }
      setErrorMessage("");

      // Check if any new document type has an empty name
      // const hasEmptyName = documentTypes.some(
      //   (doc) => doc.isNew && !doc.name.trim()
      // );
      // if (hasEmptyName) {
      //   setErrorMessage("Please provide a name for all new document types.");
      //   return;
      // }

      if (isUpdated) {
        // Prepare payload for API
        const payload = {
          sponsorId: sponsorId,
          ecosystemId: localStorage.getItem("ecosystemId"),
          documentTypesList: isAnyToggleOn.map((doc) => ({
            documentTypeID: doc.id,
            documentTypeName: doc.name?.trim() || doc.name,
            newDocumentTypeName: doc.newDocumentTypeName?.trim() || doc.newDocumentTypeName,
            activated: doc.selected,
            markForDelete: false,
            documentCategoryID: doc.selectedCategory, // Include selected category ID
            // Add any other required fields based on your API requirements
          })),
        };

        // Call API to update document types
        dispatch({ type: "OPEN_BACKDROP" });
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDocumentTypes`;
        const response = await axios.post(url, payload);

        if (
          response.data?.status === true ||
          response.data?.status === "true"
        ) {
          //console.log("Document types updated successfully");
 fetchDocumentTypes();
 setIsUpdated(false); 
          // Move to next tab if props.handleNext exists
        
        } else {
          setErrorMessage(
            response.data?.message || "Failed to update document types"
          );
        }
      } 
    } catch (error) {
      console.error("Error saving document types:", error);
      setErrorMessage("An error occurred while saving. Please try again.");
    } finally {
      dispatch({ type: "DESTROY_BACKDROP" });
      setSaveLoading(false);
    }
  };
  const handleDelete = async (index) => {
    try {
      const documentToDelete = documentTypes[index]; // Get the document type to delete
      if (documentToDelete.isNew) {
        setDocumentTypes((prev) => prev.filter((_, i) => i !== index));
        //console.log("New document type deleted without API call.");
        return;
      }
      // Prepare the payload for the API
      const payload = {
        sponsorId: sponsorId, // Example sponsorId
        ecosystemId: localStorage.getItem("ecosystemId"), // Example ecosystemId
        documentTypesList: [
          {
            documentTypeID: documentToDelete.id, // ID of the document type
            documentTypeName: documentToDelete.name?.trim() || documentToDelete.name, // Trim name
            activated: documentToDelete.selected, // Current activation state
            markForDelete: true, // Mark the document type for deletion
            newDocumentTypeName: documentToDelete.newDocumentTypeName?.trim() || documentToDelete.newDocumentTypeName, // Trim new name
          },
        ],
      };

      //console.log(payload, "Payload for deleting document type"); // Debugging

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDocumentTypes`; // API endpoint
      const response = await axios.post(url, payload);
    

      if (response.data?.status === true || response.data?.status === "true") {
        //console.log("Document type deleted successfully:", response.data);
          fetchDocumentTypes();
props.setDocumentType((prev) => !prev);
        // Update the state to remove the deleted document type
        setDocumentTypes((prev) => prev.filter((_, i) => i !== index));
      } else {
        console.error(
          "Failed to delete document type:",
          response.data?.message
        );
        setError(response.data?.message || "Failed to delete document type.");
      }
    } catch (error) {
      console.error("Error deleting document type:", error);
      setError("An error occurred while deleting the document type.");
    }
  };

  const handleBack = () => {
    if (props.handleBack) {
      props.handleBack();
    }
  };
  const handleCancel = (index) => {
    const documentToCancel = documentTypes[index];

    // If the document type is new, remove it from the state
    if (documentToCancel.isNew) {
      setDocumentTypes((prev) => prev.filter((_, i) => i !== index));
      //console.log("New document type removed.");
      return;
    }

    // If the document type is not new, revert its changes
    setDocumentTypes((prev) =>
      prev.map((doc, i) =>
        i === index
          ? {
              ...doc,
              name: doc.originalName || doc.name, // Revert to original name
              selected: doc.originalSelected || doc.selected, // Revert to original selected state
              isUpdated: false, // Mark as not updated
            }
          : doc
      )
    );
    
  };

   const handleResetChanges = () => {
    if (isUpdated) {
       fetchDocumentTypes(); // Re-fetch the document types from the backend
      setIsUpdated(false); // Reset the update state
      setErrorMessage(""); // Clear any error messages
    }
  };

  return (
    <div>
          <div
      className={classes.container}
      style={{ display: "flex", flexDirection: "column", gap: "24px" }}
    >
      {/* Table and Buttons Section */}
      <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
      <Table className={`${classes.table} default-table-styles`}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Document Type</TableCell>
             <TableCell className={classes.headerCell} style={{whiteSpace: 'nowrap'}}>Renamed Document Type</TableCell>
            <TableCell className={classes.headerCell} style={{whiteSpace: 'nowrap'}}>
              Document Type ID
            </TableCell>
            <TableCell className={classes.headerCell}>Document Category</TableCell>
            <TableCell className={classes.headerCell}>Action</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            // Show loading spinner when loading is true
            <TableRow>
              <TableCell colSpan={6}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            // Show error message when error exists
            <TableRow>
              <TableCell colSpan={6}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : documentTypes.length === 0 ? (
            // Show "No Data" message when documentTypes is empty
            <TableRow>
              <TableCell colSpan={6}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Document Types Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            // Render document types when data is available
            documentTypes.map((doc, index) => (
              <TableRow key={doc.id}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={doc.selected} // Bind to the selected state
                    onChange={() => handleToggle(index)} // Handle toggle changes
                    classes={{ switchBase: classes.switchBase }}
                    disabled={!canEdit}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {doc.isNew ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        placeholder="Type Text Here"
                        value={doc.name}
                        onChange={(e) =>
                          handleNameChange(index, e.target.value)
                        } // Handle name changes
                        onBlur={(e) =>
                          handleNameChange(index, e.target.value.trim())
                        } // Trim on blur
                        className={classes.textField}
                        variant="outlined"
                        inputProps={{
                          onBeforeInput: (e) => {
                            const allowedChars = /^[a-zA-Z0-9\s\-\/()&]+$/;
                            if (!allowedChars.test(e.data)) {
                              e.preventDefault();
                            }
                          }
                        }}
                      />
                      <div className={classes.inlineActions}>
                        <IconButton
                          onClick={() => handleCancel(index)}
                          className={classes.iconBtn}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => saveDocumentType(index)}
                          className={classes.iconBtn}
                          disabled={
                            !doc.name?.trim() ||
                            !doc.selectedCategory || // Disable if no category selected
                            savingStates[index] ||
                            documentTypes.some(
                              (d, i) =>
                                i !== index &&
                                d.name?.trim().toLowerCase() === doc.name?.trim().toLowerCase()
                            )
                          }
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    doc.name // Display the name for existing document types
                  )}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {!doc.isNew && (
                    editingIndex === index ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onBlur={e => setRenameValue(e.target.value.trim())}
                          autoFocus
                          size="small"
                          variant="outlined"
                          placeholder="Rename Document Type"
                          className={classes.textField}
                          style={{ marginRight: 8 }}
                          inputProps={{
                            onBeforeInput: (e) => {
                              const allowedChars = /^[a-zA-Z0-9\s\-\/()&]+$/;
                              if (!allowedChars.test(e.data)) {
                              e.preventDefault();
                                }
                              }
                            }}
                        />
                        <div className={classes.inlineActions}>
                          <IconButton
                            onClick={() => {
                              setEditingIndex(null);
                              setRenameValue(""); // Clear the rename value on cancel
                            }}
                            className={classes.iconBtn}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={async () => {
                              const updated = [...documentTypes];
                              updated[index].newDocumentTypeName = renameValue.trim(); // Trim leading and trailing spaces
                              updated[index].isUpdated = true;
                              setDocumentTypes(updated);
                              setIsUpdated(true);
                              setEditingIndex(null);
                              setRenameValue(""); // Clear the rename value after save
                              await handleSaveChanges();
                            }}
                            className={classes.iconBtn}
                            disabled={!renameValue.trim()} // Disable save if empty after trim
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {doc.newDocumentTypeName || ""}
                          {canEdit && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingIndex(index);
                              setRenameValue(doc.newDocumentTypeName || "");
                            }}
                            style={{ marginLeft: 4 }}
                          >
                            <EditIcon style={{ color: "orange" }} />
                          </IconButton>
                          )}
                        </span>
                    )
                  )}
                </TableCell>
                <TableCell className={classes.bodyCell}>{doc.id}</TableCell>
                <TableCell className={classes.bodyCell}>
                  <FormControl className={classes.dropdown}>
                    <Select
                      value={doc.selectedCategory || ""}
                      onChange={(e) => handleDocumentCategoryChange(index, e.target.value)}
                      disabled={!doc.selected} // Only enabled when toggle is on
                      variant="outlined"
                      displayEmpty
                      className={!doc.selected ? classes.disabledDropdown : classes.enabledDropdown}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: "#999" }}>Select from the list</span>;
                        }
                        const selectedCategory = documentCategories.find(cat => cat.id == selected);
                        // Only show the category name if it's activated, otherwise show "Select from the list"
                        return (selectedCategory && selectedCategory.selected) ? selectedCategory.name : <span style={{ color: "#999" }}>Select from the list</span>;
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                    >
                      {(documentCategories || [])
                        .filter(cat => cat.selected) // Only show activated categories
                        .length > 0 ? (
                        (documentCategories || [])
                          .filter(cat => cat.selected)
                          .map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))
                      ) : (
                        <MenuItem value="" disabled>
                          No categories available
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell className={classes.bodyCell} align="center">
                  {canEdit && (doc.isNew == true || (doc.type === "New" && doc.lastUpdatedBy !== "support@schrocken.com")) && (
                    <IconButton onClick={() => handleDelete(index)}>
                      <DeleteIcon className={classes.deleteIcon} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
<div
                className={classes.buttonContainer}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "16px", // Adjusted gap between buttons
                  marginTop: "3px",
                  marginLeft: "0rem", // Added margin to create space between table and buttons
                }}
              >
                <Button
                  className={classes.saveButton}
                  type="button"
                  onClick={handleSaveChanges}
                  disabled={!isUpdated || saveLoading || !canEdit}
                  style={{
    
    opacity: !isUpdated || !canEdit ? 0.7 : 1, // Fade out when disabled
    color: "#fff", 
  }}
                >
                  {saveLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Saving...
                      <CircularProgress size={16} style={{ color: '#fff' }} />
                    </div>
                  ) : (
                    "Save Selection"
                  )}
                </Button>
      
                <Button
                  className={classes.resetChangesButton}
                  type="button"
                  onClick={handleResetChanges}
                   disabled={!isUpdated || !canEdit} // or whatever your "no changes" condition is
  style={{
    opacity: !isUpdated || !canEdit ? 0.8 : 1, // Fade out when disabled
  }}
                >
                  Reset Selection
                </Button>
              </div>
            </div>
            </div>
      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}

      {canEdit && (
        <Button onClick={handleAddNew} className={classes.addButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Document Type
        </Button>
      )}

       {/* <div className={classes.footer}>
        <Button
          onClick={() => {
            props.handleBack();
          }}
          className={classes.backButton}
        >
          &lt; Back
        </Button>
        <StyledButton
          onClick={() => {
            if (isUpdated) {
              handleNextClick(); // Call API and move to the next tab if updates are made
            } else {
              props.handleNext(); // Directly move to the next tab if no updates are made
            }
          }}
          className={classes.saveButton}
        >
          Save & Next
        </StyledButton>
      </div> */}
    </div>
  );
};

export default DocumentTypeTab;
